import { useState } from 'react';
import { Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { Select } from '@/components/ui/select';
import { AttachmentList } from './AttachmentList';
import { errorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import {
  uploadFinanceReceipt,
  useCreateFinanceSubmission,
  useSendFinanceSubmission,
  useUpdateFinanceSubmission,
} from '@/lib/finance';
import { fromMinor, toMinor } from '@/lib/money';
import type { FinanceExpected, FinanceSubmissionRefs, FinanceSubmissionWrite } from '@/api/generated/types.gen';

const RECEIPT_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';

export interface SubmissionFormProps {
  /** GET /finance/submissions/refs — the ONLY reference data this form (and
   * the finance_submitter role it is built for) may read; see §16 item 12. */
  refs: FinanceSubmissionRefs;
  /** Edit an existing DRAFT (finance.submissions.tsx) instead of starting a
   * new one — prefills every field and skips straight to PATCH on save. */
  initial?: FinanceExpected;
  /** initial's already-registered receipts — ListMyFinanceSubmissions
   * carries these (task 22), there being no separate lookup for them. */
  initialAttachments?: readonly { id: string; name?: string }[];
  /** Fired after a successful Save draft OR Send, with the row as it now
   * stands — the caller tells the two apart by `status` ('draft' vs
   * 'planned') rather than this form taking a position on what happens next
   * (toast, navigate, close a dialog: all caller concerns). */
  onDone?: (submission: FinanceExpected) => void;
  onCancel?: () => void;
}

/**
 * The phone submission form (spec §8.3/§21) — filled in one-handed, in the
 * field, not at a desk. Every control is a large tap target (size="lg");
 * currency defaults to RSD with a two-button switch to EUR, not a dropdown,
 * for the same reason. Two rules drive the shape:
 *
 *  - No operation_id anywhere: FinanceSubmissionWrite/rebuild carry none —
 *    idempotency for a submission is by RESOURCE STATE (create makes a
 *    draft; a second create makes a second draft, which is a nuisance, not
 *    a double payment — nothing here moves money) rather than by a
 *    replayable key the way settle/batch/split do.
 *  - "Add photo" uploads only AFTER a draft exists (spec §8.3's own
 *    sequence): the file input's onChange handler ensures the draft first
 *    (creating it from whatever the form currently holds, or saving an
 *    edit to one that already exists), then uploads — never the reverse.
 *    This form does its own upload loop rather than reusing
 *    ReceiptUploadButton: that component's `target` prop is fixed at
 *    render time, which cannot express "resolve the target lazily, on
 *    first use" — the two components solve related but different problems.
 */
export function SubmissionForm({ refs, initial, initialAttachments, onDone, onCancel }: SubmissionFormProps) {
  const [amount, setAmount] = useState(() =>
    initial ? fromMinor(initial.amount_minor, initial.currency) : '',
  );
  const [currency, setCurrency] = useState<'RSD' | 'EUR'>(initial?.currency === 'EUR' ? 'EUR' : 'RSD');
  const [categoryId, setCategoryId] = useState(initial?.category_id ?? refs.categories[0]?.id ?? '');
  const [apartmentId, setApartmentId] = useState(initial?.apartment_id ?? '');
  const [counterpartyId, setCounterpartyId] = useState(initial?.counterparty_id ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [submissionId, setSubmissionId] = useState<string | null>(initial?.id ?? null);
  const [attachments, setAttachments] = useState<{ id: string; name?: string }[]>(
    initialAttachments ? [...initialAttachments] : [],
  );
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const create = useCreateFinanceSubmission();
  const update = useUpdateFinanceSubmission();
  const send = useSendFinanceSubmission();
  const busy = create.isPending || update.isPending || send.isPending || uploading;

  const minor = currency ? toMinor(amount, currency) : null;
  const problems: string[] = [];
  if (minor === null || minor <= 0) problems.push('Enter the amount.');
  if (!categoryId) problems.push('Choose an article.');

  function buildBody(): FinanceSubmissionWrite {
    return {
      amount_minor: minor as number,
      currency,
      category_id: categoryId,
      apartment_id: apartmentId || undefined,
      counterparty_id: counterpartyId || undefined,
      note: note.trim() || undefined,
    };
  }

  // The one place either mutation is called — Save draft, Send and the
  // photo upload all route through this, so "create vs update" is decided
  // in exactly one spot (whether submissionId is already set). Callback
  // style (mutate + onSuccess/onError), not mutateAsync/await — the house
  // pattern every other finance form's submit uses (MovementForm,
  // OpeningBalanceDialog).
  //
  // This was not a style preference: an earlier version of this function
  // used `await create.mutateAsync(...)` and, in handleSend, chained a
  // second `await send.mutateAsync(...)` after it. Under plain `vitest
  // run` the mutationFn was deterministically not invoked — every run,
  // the assertion failed only after the full waitFor timeout elapsed, not
  // as an occasional flake. Adding a `console.log` anywhere in the test,
  // or running with `--reporter=verbose`, made the exact same test pass
  // reliably. Root cause not established (root cause investigation was
  // stopped once the callback rewrite was verified green across 9
  // consecutive full-suite runs under the plain default reporter — the
  // same command the CI/verification step uses); see the task report for
  // the reproduction recipe. Treat `mutateAsync`+`await` chained into a
  // second mutation as suspect in this codebase's test environment until
  // someone roots it out; prefer this callback form instead.
  function ensureDraft(onSuccess: (draft: FinanceExpected) => void, onError: (err: unknown) => void) {
    const body = buildBody();
    if (submissionId) {
      update.mutate({ id: submissionId, body }, { onSuccess, onError });
      return;
    }
    create.mutate(body, {
      onSuccess: (created) => {
        setSubmissionId(created.id);
        onSuccess(created);
      },
      onError,
    });
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    // Same guard Save draft and Send apply before calling ensureDraft — a
    // photo tapped before the amount/article are filled in must not send
    // buildBody()'s `amount_minor: null` to the wire (a 422 after the
    // photo picker has already closed, with no field to point the operator
    // back to).
    if (problems.length > 0) {
      setFailure('Enter the amount and choose an article first.');
      return;
    }
    setFailure(null);
    setUploading(true);
    ensureDraft(
      (draft) => {
        void uploadFilesSequentially(Array.from(files), draft.id);
      },
      (err) => {
        setFailure(errorMessage(err));
        setUploading(false);
      },
    );
  }

  // uploadFinanceReceipt is a plain async function (not a React Query
  // mutation — see its own doc comment in lib/finance.ts), so the
  // sequential-stop-on-first-failure loop is ordinary await, same shape as
  // ReceiptUploadButton's.
  async function uploadFilesSequentially(files: File[], forSubmissionId: string) {
    try {
      for (const file of files) {
        const attId = await uploadFinanceReceipt(file, { submission_id: forSubmissionId });
        setAttachments((prev) => [...prev, { id: attId, name: file.name }]);
      }
    } catch (err) {
      setFailure(errorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  function handleSaveDraft() {
    if (problems.length > 0) {
      setFailure('Enter the amount and choose an article first.');
      return;
    }
    setFailure(null);
    ensureDraft(
      (draft) => onDone?.(draft),
      (err) => setFailure(errorMessage(err)),
    );
  }

  function handleSend() {
    if (problems.length > 0) {
      setFailure('Enter the amount and choose an article first.');
      return;
    }
    setFailure(null);
    ensureDraft(
      (draft) => {
        send.mutate(draft.id, {
          onSuccess: (sent) => onDone?.(sent),
          onError: (err) => setFailure(errorMessage(err)),
        });
      },
      (err) => setFailure(errorMessage(err)),
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div role="group" aria-label="Currency" className="flex gap-2">
        <Button
          type="button"
          size="lg"
          variant={currency === 'RSD' ? 'primary' : 'secondary'}
          onClick={() => setCurrency('RSD')}
          aria-pressed={currency === 'RSD'}
        >
          RSD
        </Button>
        <Button
          type="button"
          size="lg"
          variant={currency === 'EUR' ? 'primary' : 'secondary'}
          onClick={() => setCurrency('EUR')}
          aria-pressed={currency === 'EUR'}
        >
          EUR
        </Button>
      </div>

      <MoneyInput label="Amount" currency={currency} value={amount} onChange={setAmount} />

      <Select
        label="Article"
        value={categoryId}
        placeholder="Pick an article"
        options={refs.categories.map((c) => ({ value: c.id, label: c.name }))}
        onChange={(e) => setCategoryId(e.target.value)}
      />

      <Select
        label="Apartment (optional)"
        value={apartmentId}
        options={[{ value: '', label: 'None' }, ...refs.apartments.map((a) => ({ value: a.id, label: a.title }))]}
        onChange={(e) => setApartmentId(e.target.value)}
      />

      <div>
        <Select
          label="Paid to (optional)"
          value={counterpartyId}
          disabled={refs.counterparties.length === 0}
          options={[
            { value: '', label: 'None' },
            ...refs.counterparties.map((c) => ({ value: c.id, label: c.name })),
          ]}
          onChange={(e) => setCounterpartyId(e.target.value)}
        />
        {refs.counterparties.length === 0 && (
          <p className="mt-1 text-xs text-muted">
            You have not recorded anyone here yet — leave this blank, or ask finance to add one. This
            list fills in from your own past submissions.
          </p>
        )}
      </div>

      <Input label="Note (optional)" multiline value={note} onChange={(e) => setNote(e.target.value)} />

      <div>
        <label
          htmlFor="submission-photo"
          aria-busy={uploading || undefined}
          className={cn(
            'inline-flex h-12 cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border border-border bg-card px-6 text-[1.125rem] font-semibold text-primary transition',
            'hover:bg-[var(--ollio-navy-light)] hover:border-primary',
            uploading && 'pointer-events-none opacity-55',
          )}
        >
          <Paperclip className="h-4 w-4" aria-hidden="true" />
          {uploading ? 'Uploading…' : 'Add photo'}
        </label>
        <input
          id="submission-photo"
          type="file"
          accept={RECEIPT_ACCEPT}
          capture="environment"
          multiple
          disabled={uploading}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
          className="hidden"
        />
        <AttachmentList items={attachments} className="mt-2 flex flex-col gap-1" />
      </div>

      {problems.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-sm text-[var(--ollio-gold-deep)]">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {failure && (
        <p role="alert" className="text-sm text-destructive">
          {failure}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <Button type="button" variant="primary" size="lg" onClick={handleSend} loading={busy} disabled={busy}>
          Send
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="lg"
          onClick={handleSaveDraft}
          loading={busy}
          disabled={busy}
        >
          Save draft
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" size="lg" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
